import type { InvitationSignInOptionsResponse } from "@repo/app-types/api/user";
import { type FC, useEffect, useState } from "react";
import {
  fetchInvitationSignInOptions,
  isInvalidInvitationError,
  sendInvitationMagicLink,
} from "../../api/invitationSignInOptions.js";
import { type OAuthProvider, startOAuthSignIn } from "../../api/oauthSignIn.js";
import { requiresPopupAuth } from "../../api/popupAuth.js";
import { startSsoSignInWithProviderId } from "../../api/signInActions.js";
import { useAuthPopupSession } from "../../hooks/useAuthPopupSession.js";
import {
  type InvitationSignInMode,
  InvitationSignInScreen,
} from "./invitationSignInScreen.js";
import { SignInPage } from "./signInPage.js";

interface InvitationSignInPageProps {
  invitationToken: string;
}

type FetchState =
  | { status: "loading" }
  | { status: "ready"; options: InvitationSignInOptionsResponse }
  | { status: "invalid" }
  | { status: "fallback" };

/**
 * Container for the invitation sign-in screen. Looks up the invited company's
 * accepted auth methods pre-auth, then feeds a view model to
 * {@link InvitationSignInScreen} and owns the Supabase auth side effects. The
 * email magic link is sent server-side (keyed off the token), so this client
 * never handles the invitee's full address.
 */
export const InvitationSignInPage: FC<InvitationSignInPageProps> = ({
  invitationToken,
}) => {
  const [fetchState, setFetchState] = useState<FetchState>({
    status: "loading",
  });
  const [mode, setMode] = useState<InvitationSignInMode>("options");
  const [isSendingMagicLink, setIsSendingMagicLink] = useState(false);
  const [isMagicLinkSent, setIsMagicLinkSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Detect iframe embedding once, out of the render phase.
  const [shouldUsePopupAuth] = useState(requiresPopupAuth);

  // The fallback branch below hands the whole flow to SignInPage, which brings
  // its own listener. Staying subscribed here too would race it to redeem the
  // single-use auth code, failing one of the two exchanges.
  useAuthPopupSession({
    isEnabled: shouldUsePopupAuth && fetchState.status !== "fallback",
    onError: setErrorMessage,
  });

  useEffect(() => {
    let cancelled = false;
    setFetchState({ status: "loading" });
    void (async () => {
      try {
        const options = await fetchInvitationSignInOptions(invitationToken);
        if (!cancelled) {
          setFetchState({ status: "ready", options });
        }
      } catch (error) {
        if (!cancelled) {
          setFetchState(
            isInvalidInvitationError(error)
              ? { status: "invalid" }
              : { status: "fallback" },
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [invitationToken]);

  // Transient/network failure: fall back to standard sign-in rather than block a
  // legitimate invitee. Post-auth, GET /user re-evaluates the session and shows
  // the mismatch screen if the chosen method isn't accepted.
  if (fetchState.status === "fallback") {
    return <SignInPage />;
  }

  const options = fetchState.status === "ready" ? fetchState.options : null;

  const handleOAuthSignIn = (provider: OAuthProvider) => {
    setErrorMessage(null);
    void (async () => {
      const { error } = await startOAuthSignIn(provider, {
        usePopup: shouldUsePopupAuth,
      });
      if (error) {
        setErrorMessage("Couldn't start sign-in. Please try again.");
      }
    })();
  };

  const handleSsoSignIn = () => {
    const providerId = options?.sso_provider_ids[0] ?? null;
    if (!providerId) {
      return;
    }
    setErrorMessage(null);
    void (async () => {
      const { error } = await startSsoSignInWithProviderId(providerId, {
        usePopup: shouldUsePopupAuth,
      });
      if (error) {
        setErrorMessage(error);
      }
    })();
  };

  const handleSendMagicLink = () => {
    setErrorMessage(null);
    setIsSendingMagicLink(true);
    void (async () => {
      const { error } = await sendInvitationMagicLink(invitationToken);
      if (error) {
        setErrorMessage(error);
      } else {
        setIsMagicLinkSent(true);
      }
      setIsSendingMagicLink(false);
    })();
  };

  return (
    <InvitationSignInScreen
      status={fetchState.status}
      mode={mode}
      companyName={options?.company_name ?? null}
      acceptedMethods={options?.accepted_methods ?? []}
      ssoProviderIds={options?.sso_provider_ids ?? []}
      maskedEmail={options?.recipient_email_masked ?? null}
      isSendingMagicLink={isSendingMagicLink}
      isMagicLinkSent={isMagicLinkSent}
      errorMessage={errorMessage}
      onGoogleSignIn={() => handleOAuthSignIn("google")}
      onMicrosoftSignIn={() => handleOAuthSignIn("azure")}
      onSsoSignIn={handleSsoSignIn}
      onSelectMagicLink={() => {
        setErrorMessage(null);
        setMode("magicLink");
      }}
      onBackToOptions={() => {
        setErrorMessage(null);
        setIsMagicLinkSent(false);
        setMode("options");
      }}
      onSendMagicLink={handleSendMagicLink}
      onResetMagicLink={() => setIsMagicLinkSent(false)}
      onGoToSignIn={() => {
        window.location.href = "/";
      }}
    />
  );
};
